// src/data/officeTermsQueries.js
//
// THE ONE PLACE AN officeTerms LISTING IS SHAPED - the twin of claimQueries.js, for the same reason.
//
// 2026-09-25, task list section 412. `officeTerms` was subscribed with NO filter in two separate
// files - src\hooks\useSeatsOfRecord.js (closed as A10 earlier the same day) and
// src\components\SeatsPanel.js (missed by that fix, and found by the deeper sweep an hour later).
// Fixing the second one by copying the first would have put the same twenty lines in two files, and
// SS XI says a fact in two places is a defect. So the shape lives here and both read it.
//
// WHAT THE SHAPE HAS TO GET RIGHT, and it is not obvious:
//
//   1. A GENERAL COMMITTEE IS NOBODY'S CHILD. The offices listing asks `parentOffice == <committee>`,
//      and GO-342's own office document carries `parentOffice: null` - so the committee's own office
//      is NOT in its own scoped set, and its General Chairperson's term would be dropped from any id
//      list built naively from that query. `officeTermIdsFor()` adds the committee's own id, always.
//
//   2. FIRESTORE'S `in` TAKES AT MOST 30. A committee with 30 or more seats would not merely be
//      slow - the query would be rejected, and the screen would show no terms at all. The ids are
//      chunked, and the chunks are merged.
//
//   3. A HALF-ANSWERED SET IS WORSE THAN A LATE ONE. Publishing the first chunk before the others
//      have answered renders a held seat as vacant for as long as the rest are in flight. A vacancy
//      that is not one is exactly the kind of false statement PD-0 forbids, so nothing is published
//      until every chunk has replied at least once.
//
// NOT A SECURITY BOUNDARY, AND THIS MATTERS. firestore.rules has
// `match /officeTerms/{termId} { allow read: if isAuth(); }` - by design SS5.4, "a member must be
// able to see who held authority over his claim." The unscoped read leaked nothing and was PROVED
// to be allowed (scripts\audit-screen-queries.js, HTTP 200 with a real General Chairperson token).
// This module is about SCALE ONLY: without it, every screen that shows a seat reads every term of
// every committee on the platform. Do not describe it as a hole being closed - it was not one.
import { collection, query, where, onSnapshot } from 'firebase/firestore';

export const IN_LIMIT = 30;   // Firestore's cap on the values in an `in` filter

// The office ids whose terms a committee's screens may need: its own office, plus every office the
// `offices` listing returned for it. Deduplicated, and the committee is first so the set is stable.
export function officeTermIdsFor(committee, offices) {
  if (!committee) return [];
  const seen = [committee];
  (offices || []).forEach((o) => {
    const id = o && (o.officeId || o.id);
    if (id && seen.indexOf(id) === -1) seen.push(id);
  });
  return seen;
}

// The queries, chunked. Exported separately so a test or a verifier can read the shape without
// subscribing to anything - the same courtesy claimQueries.js extends.
export function officeTermQueries(db, officeIds) {
  const ids = (officeIds || []).filter(Boolean);
  const out = [];
  for (let i = 0; i < ids.length; i += IN_LIMIT) {
    out.push(query(collection(db, 'officeTerms'), where('officeId', 'in', ids.slice(i, i + IN_LIMIT))));
  }
  return out;
}

// Subscribe to every chunk and hand back ONE merged array, once, when they have all answered.
// Returns an unsubscribe function. onError is called with the Firestore error; a caller that
// discards it is telling its user "there are no terms" when the truth is "the read was refused",
// which is the A3 failure this platform has now made three times.
export function subscribeOfficeTerms(db, officeIds, onTerms, onError) {
  const qs = officeTermQueries(db, officeIds);
  if (!qs.length) { onTerms([]); return () => {}; }
  const byChunk = qs.map(() => null);
  const unsubs = qs.map((q, i) => onSnapshot(q,
    (s) => {
      byChunk[i] = s.docs.map((d) => ({ termId: d.id, ...d.data() }));
      if (byChunk.every((c) => c !== null)) onTerms([].concat.apply([], byChunk));
    },
    (e) => { if (onError) onError(e); }));
  return () => unsubs.forEach((u) => u());
}
